import React from 'react'
import { DialogTitle, DialogContent, DialogActions } from '@mui/material'
import ResponsiveDialog from 'components/ResponsiveDialog'
import Button from 'components/Button'
import { useForm, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { graphql, useFragment } from 'react-relay'
import { UserSkillsEditDialog_User$key } from '__generated__/UserSkillsEditDialog_User.graphql'
import { UserSkillsEditDialog_UpdateUserMutation } from '__generated__/UserSkillsEditDialog_UpdateUserMutation.graphql'
import SkillSuggestionsButton from 'components/SkillsInput/components/SkillSuggestionsButton'
import { SkillSuggestionsForm } from 'components/SkillsInput/components/SkillSuggestionsButton/SkillSuggestionsForm'
import { arrayFilterNonNullable } from 'services/array'
import { useBoolean } from 'hooks/state'
import FormSubmitButton from 'components/FormSubmitButton'
import Form from 'components/Form'
import SkillsInput, { Skill } from './SkillsInput'

interface IUserSkillsEditDialogProps {
  open: boolean
  onClose: () => void
  user: UserSkillsEditDialog_User$key
}

interface FormFields {
  userSkills: Array<Skill>
}

function UserSkillsEditDialog(props: IUserSkillsEditDialogProps) {
  const { open, onClose, user: userFragmentRef } = props

  const user = useFragment(graphql`
    fragment UserSkillsEditDialog_User on User {
      id
      profile {
        industry {
          id
          name
        }
      }
      userSkills {
        experience
        skill {
          id
          name
        }
      }
    }
  `, userFragmentRef)

  const selectedIndustry = user?.profile?.industry?.id && user?.profile?.industry?.name ? {
    id: user.profile.industry.id,
    name: user.profile.industry.name,
  } : null

  const form = useForm<FormFields, UserSkillsEditDialog_UpdateUserMutation>({
    defaultValues: {
      userSkills: user?.userSkills?.map(skill => skill.skill ? {
        experience: skill.experience || 1,
        id: skill.skill.id,
        name: skill.skill.name,
      } : null)?.filter(arrayFilterNonNullable) || [],
    },
    submitHandlers: useMutationBasedFormSubmitHandlers(graphql`
      mutation UserSkillsEditDialog_UpdateUserMutation($input: UpdateUserInput!) {
        updateUser(input: $input) {
          user {
            userSkills {
              experience
              skill {
                id
                name
              }
            }
          }
        }
      }
    `, values => ({
      input: {
        skills: values.userSkills.map(skill => ({
          experience: skill.experience,
          skillId: skill.id,
        })),
      },
    }), {
      successMessage: 'Skills updated successfully',
      verifySuccess: (result) => {
        return Boolean(result?.updateUser?.user?.userSkills)
      },
      onSuccess: () => {
        onClose()
      },
    }),
  })

  const {
    setFalse: setSkillsFormClosed,
    toggle: toggleSkillsFormOpened,
    value: skillsFormOpened,
  } = useBoolean()

  const userSkillsField = form.useFormField('userSkills', 'selection')

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md">
      <Form form={form}>
        <DialogTitle>
          Type all your skills and select your experience
        </DialogTitle>

        <DialogContent>
          {!selectedIndustry && (
          <div>
            First select your Area of Expertise to see associated skills.
          </div>
          )}

          {selectedIndustry && (
          <React.Fragment>
            <SkillsInput
              selectedIndustryId={selectedIndustry.id}
              {...userSkillsField}
              placeholder="Type to add a skill..."
              label="Your Skills"
              fullWidth
              hintText={(
                <React.Fragment>
                  Can't find a skill you need? Simply{' '}
                  <SkillSuggestionsButton
                    onClick={toggleSkillsFormOpened}
                  />{' '}to the list
                </React.Fragment>
              )}
            />
            <SkillSuggestionsForm
              isOpened={skillsFormOpened}
              onClose={setSkillsFormClosed}
              mode="profile"
              industry={selectedIndustry}
            />
          </React.Fragment>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">Cancel</Button>
          <FormSubmitButton data-cy="profile-skills-close" />
        </DialogActions>
      </Form>
    </ResponsiveDialog>
  )
}

export default UserSkillsEditDialog
